(() => {
  if (customElements.get('oso-product-sound')) return;
  const glyphs = '<span class="oso-sound-mark" aria-hidden="true"><svg data-sound-play viewBox="0 0 24 24" fill="none"><path d="M8 5.5 18.5 12 8 18.5Z" stroke="currentColor" stroke-width="1.35" stroke-linejoin="round"/></svg><svg data-sound-pause viewBox="0 0 24 24" fill="none"><path d="M8 5v14M16 5v14" stroke="currentColor" stroke-width="1.5"/></svg></span>';
  class OsoProductSound extends HTMLElement {
    static active = null;
    static framed = null;
    static source(value) {
      if (!value || typeof value.url !== 'string') return null;
      let url;
      try { url = new URL(value.url, location.origin); } catch { return null; }
      if (!['https:', 'http:'].includes(url.protocol)) return null;
      if (value.kind === 'audio') return { kind: 'audio', url: url.href };
      const host = url.hostname.toLowerCase().replace(/^www\./, '');
      if (value.kind === 'youtube' && ['youtube.com', 'youtube-nocookie.com', 'youtu.be'].includes(host)) {
        const id = host === 'youtu.be' ? url.pathname.slice(1).split('/')[0] : url.pathname.startsWith('/embed/') ? url.pathname.split('/')[2] : url.searchParams.get('v');
        if (!/^[\w-]{11}$/.test(id || '')) return null;
        const embed = new URL('https://www.youtube-nocookie.com/embed/' + id);
        embed.searchParams.set('autoplay', '1');
        embed.searchParams.set('playsinline', '1');
        embed.searchParams.set('rel', '0');
        return { kind: 'youtube', url: embed.href };
      }
      if (value.kind === 'vimeo' && ['vimeo.com', 'player.vimeo.com'].includes(host)) {
        const parts = url.pathname.split('/').filter(Boolean);
        const id = parts[0] === 'video' ? parts[1] : parts[0];
        if (!/^\d+$/.test(id || '')) return null;
        const embed = new URL('https://player.vimeo.com/video/' + id);
        const hash = url.searchParams.get('h') || (parts[0] !== 'video' ? parts[1] : '');
        if (hash && /^[a-zA-Z0-9]+$/.test(hash)) embed.searchParams.set('h', hash);
        embed.searchParams.set('autoplay', '1');
        return { kind: 'vimeo', url: embed.href };
      }
      return null;
    }
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.token = 0;
      try {
        this.words = JSON.parse(document.getElementById('oso-sound-strings').textContent);
        this.sound = OsoProductSound.source(JSON.parse(this.querySelector('[data-sound-source]').textContent));
      } catch { this.sound = null; this.words = this.words || {}; }
      this.button = this.querySelector('[data-sound-toggle]');
      if (!this.button) {
        this.insertAdjacentHTML('beforeend', '<button class="oso-sound-button" type="button" data-sound-toggle disabled>' + glyphs + '<span data-sound-label></span><span data-sound-time aria-hidden="true"></span></button><p class="oso-sound-status" data-sound-status role="status" hidden></p>');
        this.button = this.querySelector('[data-sound-toggle]');
      }
      this.label = this.querySelector('[data-sound-label]');
      this.time = this.querySelector('[data-sound-time]');
      this.status = this.querySelector('[data-sound-status]');
      this.seek = this.querySelector('[data-sound-seek]');
      if (this.sound?.kind === 'audio') {
        if (!this.seek) {
          this.seek = document.createElement('input');
          this.seek.type = 'range';
          this.seek.className = 'oso-sound-seek';
          this.seek.dataset.soundSeek = '';
          this.seek.min = '0';
          this.seek.max = '1000';
          this.seek.step = '1';
          this.status.before(this.seek);
        }
        this.seek.value = '0';
        this.seek.disabled = true;
        this.seek.setAttribute('aria-label', this.word('seek_label'));
        this.dragging = false;
        this.on(this.seek, 'pointerdown', event => {
          event.stopPropagation();
          if (!this.seek.disabled) this.dragging = true;
        });
        this.on(this.seek, 'input', event => {
          event.stopPropagation();
          if (!this.audio || !Number.isFinite(this.audio.duration) || this.audio.duration <= 0) return;
          const target = Math.max(0, Math.min(this.audio.duration, Number(this.seek.value) / 1000 * this.audio.duration));
          try { this.audio.currentTime = target; } catch { return; }
          this.updateTime();
        });
        this.on(this.seek, 'click', event => event.stopPropagation());
        this.on(this.seek, 'keydown', event => {
          event.stopPropagation();
          if (event.code === 'Space') { event.preventDefault(); this.toggle(); }
        });
        const finishSeek = () => { this.dragging = false; this.updateTime(); };
        this.on(window, 'pointerup', finishSeek);
        this.on(window, 'pointercancel', finishSeek);
        this.on(this.seek, 'change', finishSeek);
        this.on(this.seek, 'blur', finishSeek);
      }
      // Uploaded product videos play in the card's image frame, so the clip is visible before the first strike sounds.
      this.stage = null;
      if (this.sound?.kind === 'audio' && this.dataset.context === 'card' && /\.(mp4|m4v|mov|webm)$/i.test(new URL(this.sound.url).pathname)) {
        this.stage = this.closest('product-card')?.querySelector('.main-card-image') || null;
      }
      this.button.disabled = !this.sound;
      this.on(this.button, 'click', event => { event.preventDefault(); event.stopPropagation(); this.toggle(); });
      this.setState(this.sound ? 'idle' : 'unavailable');
    }
    on(target, type, handler) { target.addEventListener(type, handler, { signal: this.events.signal }); }
    word(key) { return (this.words[key] || '').replace('__PRODUCT__', this.dataset.title || ''); }
    setState(state) {
      this.phase = state;
      this.dataset.state = state;
      const external = this.sound && this.sound.kind !== 'audio';
      const label = state === 'unavailable' ? 'unavailable' : state === 'error' ? 'retry' : state === 'loading' ? 'loading' : state === 'playing' ? 'pause' : state === 'open' ? 'close' : external ? 'video' : 'listen';
      this.label.textContent = this.word(label);
      this.button.setAttribute('aria-label', this.word(label + '_label') || this.word(label));
      this.button.setAttribute('aria-busy', String(state === 'loading'));
      if (external) {
        this.button.removeAttribute('aria-pressed');
        this.button.setAttribute('aria-haspopup', 'dialog');
        this.button.setAttribute('aria-expanded', String(state === 'open'));
      } else {
        this.button.setAttribute('aria-pressed', String(state === 'playing'));
      }
      this.status.textContent = state === 'error' ? this.word('error') : '';
      this.status.hidden = state !== 'error';
    }
    activate() {
      if (OsoProductSound.active && OsoProductSound.active !== this) OsoProductSound.active.pause();
      document.dispatchEvent(new CustomEvent('oso:audio-start', { detail: { owner: this } }));
      document.querySelectorAll('video').forEach(video => { if (!video.paused) video.pause(); });
      OsoProductSound.active = this;
    }
    toggle() {
      if (!this.sound) return;
      if (this.sound.kind !== 'audio') {
        if (this.dialog) this.pause(true); else this.openVideo();
        return;
      }
      if (['loading', 'playing'].includes(this.phase)) { this.pause(false, true); return; }
      this.play();
    }
    makeAudio() {
      let audio;
      if (this.stage) {
        audio = document.createElement('video');
        audio.className = 'oso-card-video';
        audio.playsInline = true;
        audio.setAttribute('playsinline', '');
        audio.setAttribute('disablepictureinpicture', '');
        audio.setAttribute('aria-hidden', 'true');
        audio.tabIndex = -1;
        this.stage.append(audio);
      } else {
        audio = new Audio();
      }
      audio.preload = 'none';
      audio.src = this.sound.url;
      this.on(audio, 'playing', () => {
        if (OsoProductSound.active !== this) { audio.pause(); return; }
        this.showFrame(true);
        this.setState('playing');
      });
      this.on(audio, 'waiting', () => { if (OsoProductSound.active === this) this.setState('loading'); });
      this.on(audio, 'pause', () => {
        if (!audio.paused) return;
        if (OsoProductSound.active === this) OsoProductSound.active = null;
        if (['loading', 'playing'].includes(this.phase)) this.setState('paused');
      });
      this.on(audio, 'ended', () => {
        if (OsoProductSound.active === this) OsoProductSound.active = null;
        this.showFrame(false);
        this.setState('idle');
        this.updateTime();
      });
      this.on(audio, 'error', () => this.fail());
      ['timeupdate', 'loadedmetadata', 'durationchange'].forEach(type => this.on(audio, type, () => this.updateTime()));
      this.audio = audio;
      return audio;
    }
    async play() {
      const audio = this.audio || this.makeAudio();
      const token = ++this.token;
      if (this.phase === 'error' && audio.error) audio.load();
      this.activate();
      if (audio.ended) audio.currentTime = 0;
      this.setState('loading');
      try {
        await audio.play();
        if (token !== this.token || audio !== this.audio || OsoProductSound.active !== this) return;
        if (!audio.paused) this.setState('playing');
      } catch {
        if (token === this.token && audio === this.audio) this.fail();
      }
    }
    showFrame(visible) {
      if (!this.stage || !this.audio) return;
      if (visible && OsoProductSound.framed && OsoProductSound.framed !== this) OsoProductSound.framed.showFrame(false);
      if (visible) OsoProductSound.framed = this; else if (OsoProductSound.framed === this) OsoProductSound.framed = null;
      this.audio.classList.toggle('is-showing', visible);
    }
    fail() {
      this.token++;
      if (OsoProductSound.active === this) OsoProductSound.active = null;
      this.audio?.pause();
      this.showFrame(false);
      this.setState('error');
    }
    updateTime() {
      const audio = this.audio;
      if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) { this.time.textContent = ''; if (this.seek) this.seek.disabled = true; return; }
      const stamp = value => Math.floor(value / 60) + ':' + String(Math.floor(value % 60)).padStart(2, '0');
      const position = Math.max(0, Math.min(audio.duration, audio.currentTime || 0));
      const current = stamp(position);
      const duration = stamp(audio.duration);
      this.time.textContent = current + ' / ' + duration;
      if (this.seek) {
        this.seek.disabled = false;
        if (!this.dragging) this.seek.value = String(Math.round(position / audio.duration * 1000));
        this.seek.style.setProperty('--sound-progress', (position / audio.duration * 100) + '%');
        this.seek.setAttribute('aria-valuetext', this.word('seek_time').replace('__CURRENT__', current).replace('__DURATION__', duration));
      }
    }
    openVideo() {
      this.activate();
      const dialog = document.createElement('dialog');
      dialog.className = 'oso-sound-dialog';
      dialog.setAttribute('aria-label', this.word('dialog_label'));
      const header = document.createElement('div');
      header.className = 'oso-sound-dialog__header';
      const title = document.createElement('p');
      title.textContent = this.dataset.title || '';
      const close = document.createElement('button');
      close.type = 'button';
      close.textContent = this.word('close');
      const frame = document.createElement('iframe');
      frame.title = this.word('dialog_label');
      frame.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
      frame.allowFullscreen = true;
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      header.append(title, close);
      dialog.append(header, frame);
      this.on(close, 'click', () => this.pause(true));
      this.on(dialog, 'cancel', event => { event.preventDefault(); this.pause(true); });
      this.on(dialog, 'click', event => { if (event.target === dialog) this.pause(true); });
      this.dialog = dialog;
      document.body.append(dialog);
      dialog.showModal();
      frame.src = this.sound.url;
      this.setState('open');
      close.focus();
    }
    pause(restoreFocus = false, keepFrame = false) {
      this.token++;
      if (OsoProductSound.active === this) OsoProductSound.active = null;
      this.audio?.pause();
      if (!keepFrame) this.showFrame(false);
      if (this.dialog) {
        this.dialog.querySelector('iframe').src = 'about:blank';
        this.dialog.close();
        this.dialog.remove();
        this.dialog = null;
      }
      if (this.sound) this.setState('paused');
      if (restoreFocus && this.isConnected) this.button.focus({ preventScroll: true });
    }
    disconnectedCallback() {
      this.pause();
      this.events?.abort();
      this.events = null;
      if (this.audio) { this.audio.removeAttribute('src'); this.audio.load(); if (this.stage) this.audio.remove(); this.audio = null; }
    }
  }
  customElements.define('oso-product-sound', OsoProductSound);
  document.addEventListener('oso:audio-start', event => {
    if (OsoProductSound.active && event.detail?.owner !== OsoProductSound.active) OsoProductSound.active.pause();
  });
  document.addEventListener('play', event => {
    if (event.target instanceof HTMLMediaElement && OsoProductSound.active && event.target !== OsoProductSound.active.audio) OsoProductSound.active.pause();
  }, true);
  document.addEventListener('visibilitychange', () => { if (document.hidden) OsoProductSound.active?.pause(); });
  window.addEventListener('pagehide', () => OsoProductSound.active?.pause());
})();
